import React, { useState } from "react";
import { NavLink, Outlet, useLocation } from "react-router-dom";
import { Sun, Inbox, CheckSquare, Calendar, Columns, Sparkles, Settings as SettingsIcon,
         Moon, LogOut, Menu, X, Send } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useTheme } from "@/context/ThemeProvider";

const NAV = [
  { to: "/", label: "Aujourd'hui", icon: Sun, testid: "nav-today" },
  { to: "/inbox", label: "Inbox", icon: Inbox, testid: "nav-inbox" },
  { to: "/tasks", label: "Tâches", icon: CheckSquare, testid: "nav-tasks" },
  { to: "/calendar", label: "Agenda", icon: Calendar, testid: "nav-calendar" },
  { to: "/week", label: "Ma Semaine", icon: Columns, testid: "nav-week" },
  { to: "/followups", label: "Relances", icon: Send, testid: "nav-followups" },
  { to: "/ask", label: "Assistant AI", icon: Sparkles, testid: "nav-ask" },
];

function NavItems({ onClick }) {
  return (
    <nav className="flex flex-col gap-1">
      {NAV.map(({ to, label, icon: Icon, testid }) => (
        <NavLink
          key={to}
          to={to}
          end={to === "/"}
          data-testid={testid}
          onClick={onClick}
          className={({ isActive }) =>
            `flex items-center gap-3 rounded-xl px-3.5 py-2.5 text-sm font-medium transition-all duration-200
             ${isActive ? "bg-primary/10 text-primary" : "text-muted-foreground hover:text-foreground hover:bg-accent"}`
          }
        >
          <Icon className="w-[18px] h-[18px]" />
          {label}
        </NavLink>
      ))}
    </nav>
  );
}

export default function AppShell() {
  const { user, logout } = useAuth();
  const { theme, toggle } = useTheme();
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();

  const SidebarInner = (
    <>
      <div>
        <div className="flex items-center justify-between mb-8 px-1">
          <div className="font-display text-xl font-semibold tracking-tight">AIRTIME</div>
        </div>
        <NavItems onClick={() => setMobileOpen(false)} />
      </div>
      <div className="space-y-1">
        <NavLink to="/settings" data-testid="nav-settings" onClick={() => setMobileOpen(false)}
          className={({ isActive }) =>
            `flex items-center gap-3 rounded-xl px-3.5 py-2.5 text-sm font-medium transition-all
             ${isActive ? "bg-primary/10 text-primary" : "text-muted-foreground hover:text-foreground hover:bg-accent"}`}>
          <SettingsIcon className="w-[18px] h-[18px]" /> Réglages
        </NavLink>
        <button data-testid="theme-toggle" onClick={toggle}
          className="w-full flex items-center gap-3 rounded-xl px-3.5 py-2.5 text-sm font-medium text-muted-foreground hover:text-foreground hover:bg-accent transition-all">
          {theme === "dark" ? <Sun className="w-[18px] h-[18px]" /> : <Moon className="w-[18px] h-[18px]" />}
          {theme === "dark" ? "Mode clair" : "Mode sombre"}
        </button>
        <div className="mt-3 flex items-center gap-3 rounded-xl px-2 py-2 border border-border">
          <div className="w-8 h-8 rounded-full bg-primary/15 text-primary flex items-center justify-center text-sm font-semibold overflow-hidden">
            {user?.picture ? <img src={user.picture} alt="" className="w-full h-full object-cover" /> : (user?.name?.[0] || "A")}
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-sm font-medium truncate">{user?.name || "Utilisateur"}</div>
            <div className="text-xs text-muted-foreground truncate">{user?.email}</div>
          </div>
          <button data-testid="logout-button" onClick={logout} className="text-muted-foreground hover:text-rose-400 transition-colors p-1">
            <LogOut className="w-4 h-4" />
          </button>
        </div>
      </div>
    </>
  );

  return (
    <div className="flex h-screen w-full bg-background text-foreground overflow-hidden font-sans">
      {/* Desktop sidebar */}
      <aside className="w-64 border-r border-border bg-card/40 backdrop-blur-xl flex-col justify-between p-4 hidden md:flex shrink-0">
        {SidebarInner}
      </aside>

      {/* Mobile top bar */}
      <div className="md:hidden fixed top-0 inset-x-0 z-40 flex items-center justify-between px-4 h-14 border-b border-border bg-card/80 backdrop-blur-xl">
        <div className="font-display text-lg font-semibold">AIRTIME</div>
        <button data-testid="mobile-menu-toggle" onClick={() => setMobileOpen((o) => !o)} className="p-2">
          {mobileOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
        </button>
      </div>
      {mobileOpen && (
        <div className="md:hidden fixed inset-0 z-30 pt-14">
          <div className="absolute inset-0 bg-background/80 backdrop-blur-sm" onClick={() => setMobileOpen(false)} />
          <aside className="absolute left-0 top-14 bottom-0 w-72 bg-card border-r border-border flex flex-col justify-between p-4">
            {SidebarInner}
          </aside>
        </div>
      )}

      {/* Content */}
      <main key={location.pathname} className="flex-1 h-full overflow-y-auto airtime-scroll pt-14 md:pt-0 animate-fade-up">
        <div className="max-w-6xl mx-auto w-full p-5 sm:p-8 lg:p-10">
          <Outlet />
        </div>
      </main>
    </div>
  );
}
